import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBrain,
  faChartLine,
  faRocket,
  faShieldAlt,
  faUsers,
  faCog,
  faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import { GradientText } from '../ui/gradient-text';
import { useTranslation } from 'react-i18next';

const Features: React.FC = () => {
  // @ts-ignore
  const { t } = useTranslation('landing');
  const animationFrameRef = useRef<number | null>(null);
  const [progress, setProgress] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Mevcut feature içerikleri korunuyor
  const features = [
    {
      icon: faBrain,
      title: t('features.items.aiInsights.title'),
      description: t('features.items.aiInsights.description'),
      gradient: "from-purple-500 to-purple-700",
      borderColor: "border-purple-400/20",
      imageIndex: 1
    },
    {
      icon: faChartLine,
      title: t('features.items.realtimeAnalytics.title'),
      description: t('features.items.realtimeAnalytics.description'),
      gradient: "from-blue-500 to-blue-700",
      borderColor: "border-blue-400/20",
      imageIndex: 2
    },
    {
      icon: faRocket,
      title: t('features.items.autoOptimization.title'),
      description: t('features.items.autoOptimization.description'),
      gradient: "from-green-500 to-green-700",
      borderColor: "border-green-400/20",
      imageIndex: 3
    },
    {
      icon: faUsers,
      title: t('features.items.audienceAnalysis.title'),
      description: t('features.items.audienceAnalysis.description'),
      gradient: "from-pink-500 to-pink-700",
      borderColor: "border-pink-400/20",
      imageIndex: 4
    },
    {
      icon: faLightbulb,
      title: t('features.items.contentRecommendations.title'),
      description: t('features.items.contentRecommendations.description'),
      gradient: "from-yellow-500 to-yellow-700",
      borderColor: "border-yellow-400/20",
      imageIndex: 5
    },
    {
      icon: faShieldAlt,
      title: t('features.items.secureIntegration.title'),
      description: t('features.items.secureIntegration.description'),
      gradient: "from-cyan-500 to-cyan-700",
      borderColor: "border-cyan-400/20",
      imageIndex: 6
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const PROGRESS_DURATION = 5000; // 5 saniyede progress dolacak

  // Progress bar'ı doldur ve dolunca bir sonraki feature'a geç (sadece desktop'ta)
  useEffect(() => {
    // Mobile'da auto-progress çalışmasın
    const isMobile = window.innerWidth < 1024; // lg breakpoint
    if (isMobile) {
      return;
    }

    setProgress(0);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / PROGRESS_DURATION) * 100, 100);
      setProgress(newProgress);

      if (newProgress >= 100) {
        // Progress tamamlandı, bir sonraki feature'a geç
        setTimeout(() => {
          setCurrentIndex((prevIndex) => (prevIndex + 1) % features.length);
        }, 50);
      } else {
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentIndex, features.length]);

  const handleFeatureClick = (index: number) => {
    setCurrentIndex(index);
    setProgress(0);
  };

  // Swipe handlers for mobile carousel
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      // Swipe left - next feature
      setCurrentIndex((prevIndex) => (prevIndex + 1) % features.length);
      setProgress(0);
    } else if (distance < -minSwipeDistance) {
      // Swipe right - previous feature
      setCurrentIndex((prevIndex) => (prevIndex - 1 + features.length) % features.length);
      setProgress(0);
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const currentFeature = features[currentIndex];

  return (
    <section className="py-10 sm:py-12 md:py-16 bg-gradient-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-4">
            <span className="block mb-2">{t('features.title')}</span>
            <GradientText className="block text-6xl sm:text-8xl md:text-10xl font-bold" text={t('features.titleHighlight')} />
          </h2>

          <p
            className="text-base sm:text-lg md:text-xl text-white/70 max-w-3xl mx-auto px-4"
          // style={{
          //   orphans: 4,
          //   widows: 4,
          //   textWrap: 'pretty'
          // }}
          >
            {t('features.subtitle')}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-4 sm:gap-6 lg:gap-8">
          {/* Left Side - Feature List (Hidden on mobile) */}
          <div className="hidden lg:block space-y-1.5 sm:space-y-2 max-w-full">
            {features.map((feature, index) => {
              const isActive = index === currentIndex;
              const isPast = index < currentIndex;

              return (
                <div
                  key={index}
                  onClick={() => handleFeatureClick(index)}
                  className={`relative group cursor-pointer transition-all duration-300 rounded-md sm:rounded-lg overflow-hidden ${isActive
                    ? 'border-2 border-purple-400/50 shadow-lg shadow-purple-500/20'
                    : isPast
                      ? 'bg-white/5 border border-white/10 hover:bg-white/8'
                      : 'bg-white/5 border border-white/10 hover:bg-white/8'
                    }`}
                  style={{
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  {/* Progress Background - Tüm Box */}
                  <div
                    className={`absolute inset-0 z-0 ${isActive
                      ? 'bg-gradient-to-r from-purple-500/20 to-purple-700/20'
                      : isPast
                        ? 'bg-gradient-to-r from-purple-500/10 to-purple-700/10'
                        : 'bg-transparent'
                      }`}
                    style={{
                      width: isActive ? `${progress}%` : isPast ? '100%' : '0%',
                      height: '100%',
                      willChange: 'width'
                    }}
                  />

                  {/* Content */}
                  <div className="relative z-10 p-2 sm:p-2.5 flex items-center gap-2 sm:gap-2.5">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br ${feature.gradient} rounded-md sm:rounded-lg flex items-center justify-center transition-transform duration-300 ${isActive ? 'scale-105 shadow-md' : 'group-hover:scale-105'
                      }`}>
                      <FontAwesomeIcon
                        icon={feature.icon}
                        className={`text-white transition-all duration-300 ${isActive ? 'text-xs sm:text-sm' : 'text-xs sm:text-sm'
                          }`}
                      />
                    </div>

                    {/* Text Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-bold transition-all duration-300 ${isActive
                        ? 'text-white text-xs sm:text-sm md:text-base'
                        : 'text-white/70 text-xs sm:text-sm group-hover:text-white/90'
                        }`}>
                        {feature.title}
                      </h3>
                    </div>

                    {/* Active Indicator */}
                    {isActive && (
                      <div className="flex-shrink-0">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-pulse" />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right Side - Active Feature Details / Mobile Carousel */}
          <div className="relative min-h-[300px] sm:min-h-[400px] md:min-h-[500px]">
            {/* Desktop View */}
            <div className="hidden lg:block sticky top-20 sm:top-24">
              <div className="backdrop-blur-xl bg-white/5 border border-purple-400/20 rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 flex flex-col">
                {/* Image Section */}
                <div className="relative w-full mb-4 sm:mb-6 flex items-center justify-center" style={{ minHeight: '200px' }}>
                  <img
                    src={`/modules/${currentFeature.imageIndex}.png`}
                    alt={currentFeature.title}
                    className="w-full object-contain rounded-lg transition-all duration-500 animate-fade-in"
                  />
                </div>

                {/* Content Section */}
                <div className="mt-auto flex items-start gap-3 sm:gap-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${currentFeature.gradient} rounded-lg sm:rounded-xl flex items-center justify-center transition-transform duration-300`}>
                    <FontAwesomeIcon icon={currentFeature.icon} className="text-white text-lg sm:text-xl" />
                  </div>

                  {/* Text Content */}
                  <div className="flex-1 space-y-2 sm:space-y-3">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                      {currentFeature.title}
                    </h3>

                    <p
                      className="text-white/70 leading-relaxed text-sm sm:text-base md:text-lg"
                    >
                      {currentFeature.description}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Carousel View */}
            <div
              ref={carouselRef}
              className="lg:hidden relative overflow-hidden"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * 100}%)`
                }}
              >
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0 px-2"
                  >
                    <div className="backdrop-blur-xl bg-white/5 border border-purple-400/20 rounded-lg sm:rounded-xl p-4 sm:p-6 flex flex-col">
                      {/* Image Section */}
                      <div className="relative w-full mb-4 sm:mb-6 flex items-center justify-center" style={{ minHeight: '200px' }}>
                        <img
                          src={`/modules/${feature.imageIndex}.png`}
                          alt={feature.title}
                          className="w-full object-contain rounded-lg transition-all duration-500"
                        />
                      </div>

                      {/* Content Section */}
                      <div className="mt-auto flex items-start gap-3 sm:gap-4">
                        {/* Icon */}
                        <div className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${feature.gradient} rounded-lg sm:rounded-xl flex items-center justify-center transition-transform duration-300`}>
                          <FontAwesomeIcon icon={feature.icon} className="text-white text-lg sm:text-xl" />
                        </div>

                        {/* Text Content */}
                        <div className="flex-1 space-y-2 sm:space-y-3">
                          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
                            {feature.title}
                          </h3>

                          <p
                            className="text-white/70 leading-relaxed text-sm sm:text-base md:text-lg"
                          >
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation Dots */}
              <div className="flex justify-center gap-2 mt-4 sm:mt-6">
                {features.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setProgress(0);
                    }}
                    className={`transition-all duration-300 rounded-full ${index === currentIndex
                      ? 'w-2 h-2 bg-purple-400'
                      : 'w-2 h-2 bg-white/30 hover:bg-white/50'
                      }`}
                    style={{
                      minHeight: '2px',
                      minWidth: '2px',
                    }}
                    aria-label={`Go to feature ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
